import { getBasePath } from '../../utils/config';
import { safeJsonParse, buildUrl } from 'api/utils/fetchHelpers';
import {
  IaddClientTarifAPIResponse,
  IcreateTarifAPIRequestBody,
  IcreateTarifAPIResponse,
  IdeleteClientTarifAPIResponse,
  IdeleteProductsTarifAPIResponse,
  IdeleteTarifAPIResponse,
  IdownloadTarifAPIResponse,
  IgetCatalogTarifsAPIResponse,
  IgetProductsTarifsAPIResponse,
  IupdateTarifAPIRequestBody,
  IupdateTarifAPIResponse,
  IuploadProductsTarifAPIResponse,
  IuploadTarifAPIResponse,
} from './catalogTarifs.response.interface';

export const getCatalogTarifsAPI = async (
  brandId: string,
  token: string
): Promise<IgetCatalogTarifsAPIResponse> => {
  const params = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  const response = await fetch(
    buildUrl(`${getBasePath()}/tarif/tarifs`, { tenant_id: brandId }),
    params
  );
  return await safeJsonParse(response);
};

export const createTarifAPI = async ({
  body,
  token,
}: {
  body: IcreateTarifAPIRequestBody;
  token: string;
}): Promise<IcreateTarifAPIResponse> => {
  const params = {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  const response = await fetch(`${getBasePath()}/tarif/add_tarif`, params);
  return await safeJsonParse(response);
};

export const updateTarifAPI = async ({
  body,
  token,
}: {
  body: IupdateTarifAPIRequestBody;
  token: string;
}): Promise<IupdateTarifAPIResponse> => {
  const params = {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  const response = await fetch(`${getBasePath()}/tarif/update_tarif`, params);
  return await safeJsonParse(response);
};

export const deleteTarifAPI = async ({
  tarifId,
  token,
}: {
  tarifId: string;
  token: string;
}): Promise<IdeleteTarifAPIResponse> => {
  const params = {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  const response = await fetch(
    buildUrl(`${getBasePath()}/tarif/delete_tarif`, { id: tarifId }),
    params
  );
  return await safeJsonParse(response);
};

export const downloadTarifAPI = async ({
  tenantId,
  tarifId,
  pendingProducts,
  token,
}: {
  tenantId: string;
  tarifId: string;
  pendingProducts: boolean;
  token: string;
}): Promise<IdownloadTarifAPIResponse> => {
  const params = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  const response = await fetch(
    `${getBasePath()}/tarif/download?tenant_id=${tenantId}&tarif_id=${tarifId}${pendingProducts ? '&new_products' : ''}`,
    params
  );
  const data = await response.blob();
  return { response, data };
};

export const uploadProductsTarifAPI = async ({
  tenantId,
  token,
}: {
  tenantId: string;
  token: string;
}): Promise<IuploadProductsTarifAPIResponse> => {
  const params = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  const response = await fetch(
    buildUrl(`${getBasePath()}/tarif/update_product_tarifs`, { tenant_id: tenantId }),
    params
  );
  return await safeJsonParse(response);
};

export const getProductsTarifsAPI = async ({
  tarifId,
  token,
}: {
  tarifId: string;
  token: string;
}): Promise<IgetProductsTarifsAPIResponse> => {
  const params = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tarif_id: tarifId,
      limit: 1000000,
      index: 0,
    }),
  };
  const response = await fetch(`${getBasePath()}/tarif/get_product_tarifs`, params);
  return await safeJsonParse(response);
};

export const deleteProductsTarifAPI = async ({
  tarifId,
  token,
}: {
  tarifId: string;
  token: string;
}): Promise<IdeleteProductsTarifAPIResponse> => {
  const params = {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  const response = await fetch(
    buildUrl(`${getBasePath()}/tarif/delete_product_tarifs`, { tarif_id: tarifId }),
    params
  );
  return await safeJsonParse(response);
};

export const addClientTarifAPI = async ({
  clientId,
  tarifId,
  token,
}: {
  clientId: string;
  tarifId: string;
  token: string;
}): Promise<IaddClientTarifAPIResponse> => {
  const params = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_id: clientId,
      tarif_id: tarifId,
    }),
  };
  const response = await fetch(`${getBasePath()}/tarif/add_client_tarif`, params);
  return await safeJsonParse(response);
};

export const deleteClientTarifAPI = async ({
  clientTarifId,
  token,
}: {
  clientTarifId: string;
  token: string;
}): Promise<IdeleteClientTarifAPIResponse> => {
  const params = {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  const response = await fetch(
    buildUrl(`${getBasePath()}/tarif/delete_client_tarif`, { id: clientTarifId }),
    params
  );
  return await safeJsonParse(response);
};

export const uploadTarifAPI = async ({
  tarifId,
  fileName,
  token,
}: {
  tarifId: string;
  fileName: string;
  token: string;
}): Promise<IuploadTarifAPIResponse> => {
  const params = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tarif_id: tarifId,
      items: [fileName],
    }),
  };
  const response = await fetch(`${getBasePath()}/tarif/upload_tarif`, params);
  return await safeJsonParse(response);
};
